import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Loader2, TrendingDown } from 'lucide-react';
import { instrumentAPI } from '../../../../../services/api/instrumentAPI';

const CloseTradeModal = ({
  isOpen,
  onClose,
  onConfirm,
  trade,
  processing,
  formatNumber,
}) => {
  const [closePercentage, setClosePercentage] = useState(100);
  const [closeQty, setCloseQty] = useState('');
  const [mode, setMode] = useState('slider'); // 'slider' | 'manual'
  const [error, setError] = useState('');

  // Instrument constraints
  const [minSize, setMinSize] = useState(null);
  const [maxSize, setMaxSize] = useState(null);
  const [qtyStep, setQtyStep] = useState(null);
  const [priceDecimals, setPriceDecimals] = useState(null);
  const [constraintsLoading, setConstraintsLoading] = useState(false);

  // Reset when trade changes
  useEffect(() => {
    if (trade && isOpen) {
      setClosePercentage(100);
      setCloseQty('');
      setMode('slider');
      setError('');
      setQtyStep(null);
      setPriceDecimals(null);
      fetchInstrumentConstraints(trade.symbol);
    }
  }, [trade, isOpen]);

  const fetchInstrumentConstraints = async (symbol) => {
    try {
      setConstraintsLoading(true);
      const response = await instrumentAPI.getAll();
      const list = response?.data?.data?.data || response?.data?.data || response?.data || [];
      const instruments = Array.isArray(list) ? list : [];
      const inst = instruments.find(i => i.symbol === symbol);
      if (inst) {
        const min = parseFloat(inst.min_Order_Size) || 0;
        const max = parseFloat(inst.max_Order_Size) || 0;
        const step = parseFloat(inst.order_Quantity_Step) || 0;
        const decimals = parseInt(inst.decimals, 10);
        setMinSize(min > 0 ? min : null);
        setMaxSize(max > 0 ? max : null);
        setQtyStep(step > 0 ? step : null);
        setPriceDecimals(!isNaN(decimals) && decimals >= 0 ? decimals : null);
      } else {
        setMinSize(null);
        setMaxSize(null);
        setQtyStep(null);
        setPriceDecimals(null);
      }
    } catch {
      setMinSize(null);
      setMaxSize(null);
      setQtyStep(null);
      setPriceDecimals(null);
    } finally {
      setConstraintsLoading(false);
    }
  };

  if (!isOpen || !trade) return null;

  const decimals = priceDecimals != null ? priceDecimals : 5;
  const totalQty = Number(trade.quantity) || 0;
  const sliderQty = totalQty * (closePercentage / 100);
  const manualQty = Number(closeQty) || 0;
  const finalQty = mode === 'slider' ? sliderQty : Math.min(manualQty, totalQty);

  // Validation
  const validate = () => {
    if (mode === 'slider' && closePercentage === 0) return 'Select a close percentage';
    if (mode === 'manual') {
      if (!closeQty || manualQty <= 0) return 'Enter a quantity to close';
      if (manualQty > totalQty) return 'Quantity cannot exceed position size (' + formatNumber(totalQty, 2) + ')';
    }
    // Min order size check (only for partial close, not full)
    const isPartialClose = finalQty < totalQty;
    if (isPartialClose && minSize !== null && finalQty < minSize) {
      return 'Minimum close quantity is ' + formatNumber(minSize, 2) + ' (current: ' + formatNumber(finalQty, 2) + ')';
    }
    // Check remaining quantity after close — if partial, remaining must also be >= min (or 0)
    if (isPartialClose && minSize !== null && (totalQty - finalQty) > 0 && (totalQty - finalQty) < minSize) {
      return 'Remaining quantity would be ' + formatNumber(totalQty - finalQty, 2) + ', below minimum ' + formatNumber(minSize, 2) + '. Close the full position instead.';
    }
    // Step / multiplier check: close quantity must be in valid increments
    if (qtyStep !== null && qtyStep > 0 && minSize !== null && finalQty > 0 && finalQty < totalQty) {
      const remainder = (finalQty - minSize) % qtyStep;
      if (Math.abs(remainder) > 0.0001) {
        // Find nearest valid quantity below
        const nearestBelow = minSize + Math.floor((finalQty - minSize) / qtyStep) * qtyStep;
        const nearestAbove = nearestBelow + qtyStep;
        return 'Close quantity must be in increments of ' + formatNumber(qtyStep, 2) + '. Nearest valid: ' + formatNumber(nearestBelow, 2) + ' or ' + formatNumber(Math.min(nearestAbove, totalQty), 2);
      }
    }
    // Also check remaining quantity respects step (if not closing full)
    if (isPartialClose && qtyStep !== null && qtyStep > 0 && minSize !== null) {
      const remaining = totalQty - finalQty;
      if (remaining > 0) {
        const remRemainder = (remaining - minSize) % qtyStep;
        if (Math.abs(remRemainder) > 0.0001) {
          return 'Remaining quantity (' + formatNumber(remaining, 2) + ') must be in increments of ' + formatNumber(qtyStep, 2) + '. Adjust close quantity.';
        }
      }
    }
    return null;
  };

  const handleConfirm = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    onConfirm({
      tradeId: trade.tradeId,
      userId: trade.userId,
      closePercentage: mode === 'slider' ? closePercentage : undefined,
      closeQuantity: mode === 'manual' ? finalQty : undefined,
    });
  };

  const isPartialClose = finalQty < totalQty && finalQty > 0;
  const remainingQty = totalQty - finalQty;
  const violatesMin = isPartialClose && minSize !== null && (finalQty < minSize || (remainingQty > 0 && remainingQty < minSize));
  const violatesStep = isPartialClose && qtyStep !== null && qtyStep > 0 && minSize !== null && (
    Math.abs((finalQty - minSize) % qtyStep) > 0.0001 ||
    (remainingQty > 0 && Math.abs((remainingQty - minSize) % qtyStep) > 0.0001)
  );
  const showMinWarning = violatesMin || violatesStep;

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h3><TrendingDown size={15} /> Close {trade.symbol}</h3>
          <button className="pm-modal-close" onClick={onClose} disabled={processing}>
            <X size={14} />
          </button>
        </div>

        <div className="pm-modal-body">
          {/* Trade summary */}
          <div className="pm-modal-strip">
            <span className={'pm-tag pm-tag--' + trade.side}>
              {trade.side === 'buy' ? 'LONG' : 'SHORT'}
            </span>
            <span className="pm-num" style={{ fontSize: '0.8rem' }}>{formatNumber(totalQty, 2)}</span>
            <span style={{ color: 'var(--pm-text-muted)', fontSize: '0.75rem' }}>@</span>
            <span className="pm-num" style={{ fontSize: '0.75rem', color: 'var(--pm-text-sec)' }}>{formatNumber(trade.entryPrice, decimals)}</span>
            {trade.leverage && (
              <span style={{ marginLeft: 'auto', fontSize: '0.64rem', fontWeight: 600, color: 'var(--pm-text-muted)', background: 'var(--pm-bg)', padding: '2px 6px', borderRadius: 'var(--pm-r-full)' }}>
                1:{trade.leverage}x
              </span>
            )}
          </div>

          {/* Instrument constraints info */}
          {!constraintsLoading && (minSize !== null || maxSize !== null) && (
            <div style={{ fontSize: '0.65rem', color: 'var(--pm-text-muted)', marginBottom: '0.4rem', padding: '4px 8px', background: 'var(--pm-bg)', borderRadius: 'var(--pm-r-sm)' }}>
              Min: {formatNumber(minSize || 0, 2)} | Max: {maxSize ? formatNumber(maxSize, 2) : '∞'} | Step: {qtyStep ? formatNumber(qtyStep, 2) : 'any'} | Position: {formatNumber(totalQty, 2)}
            </div>
          )}

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '0.55rem' }}>
            <button
              type="button"
              className={'pm-btn ' + (mode === 'slider' ? 'pm-btn--primary' : 'pm-btn--ghost')}
              style={{ fontSize: '0.7rem', padding: '4px 12px', flex: 1 }}
              onClick={() => { setMode('slider'); setError(''); }}
              disabled={processing}
            >
              By Percentage
            </button>
            <button
              type="button"
              className={'pm-btn ' + (mode === 'manual' ? 'pm-btn--primary' : 'pm-btn--ghost')}
              style={{ fontSize: '0.7rem', padding: '4px 12px', flex: 1 }}
              onClick={() => { setMode('manual'); setError(''); }}
              disabled={processing}
            >
              By Quantity
            </button>
          </div>

          {/* Slider mode */}
          {mode === 'slider' && (
            <div style={{ marginBottom: '0.5rem' }}>
              <div className="pm-slider-header">
                <span>Close {closePercentage}%</span>
                <span className="pm-slider-sub">{formatNumber(sliderQty, 2)} qty</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={closePercentage}
                onChange={(e) => { setClosePercentage(Number(e.target.value)); setError(''); }}
                className={violatesStep ? 'pm-slider pm-slider--warn' : 'pm-slider'}
                disabled={processing}
              />
              <div className="pm-presets">
                {[25, 50, 75, 100].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    className={'pm-preset ' + (closePercentage === pct ? 'active' : '')}
                    onClick={() => { setClosePercentage(pct); setError(''); }}
                    disabled={processing}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Manual quantity mode */}
          {mode === 'manual' && (
            <div className="pm-modal-field" style={{ marginBottom: '0.5rem' }}>
              <label>
                Quantity to close
                <span style={{ fontWeight: 400, fontSize: '0.62rem', color: 'var(--pm-text-muted)', marginLeft: '6px' }}>
                  max {formatNumber(totalQty, 2)}
                </span>
              </label>
              <input
                type="number"
                min={minSize || 0}
                max={totalQty}
                step="any"
                value={closeQty}
                onChange={(e) => { setCloseQty(e.target.value); setError(''); }}
                placeholder={'0.00'}
                disabled={processing}
              />
            </div>
          )}

          {/* Summary line */}
          {finalQty > 0 && (
            <div style={{ fontSize: '0.68rem', color: 'var(--pm-text-sec)', marginBottom: '0.4rem' }}>
              Closing <strong>{formatNumber(finalQty, 2)}</strong> of {formatNumber(totalQty, 2)}
              {isPartialClose && (
                <> — remaining: <strong>{formatNumber(remainingQty, 2)}</strong></>
              )}
            </div>
          )}

          {/* Min/max warning */}
          {showMinWarning && (
            <div className="pm-warning">
              <AlertTriangle size={12} />
              <span>
                {violatesStep
                  ? 'Quantity must be in increments of ' + formatNumber(qtyStep, 2) + ' (e.g. ' + formatNumber(minSize, 2) + ', ' + formatNumber(minSize + qtyStep, 2) + ', ' + formatNumber(minSize + qtyStep * 2, 2) + ').'
                  : 'Partial close quantity (' + formatNumber(finalQty, 2) + ') must be at least ' + formatNumber(minSize, 2) + '. ' +
                    'Remaining position (' + formatNumber(remainingQty, 2) + ') must also meet the minimum or be zero.'
                }
              </span>
            </div>
          )}

          {/* General warning */}
          {!showMinWarning && finalQty > 0 && (
            <div className="pm-warning">
              <AlertTriangle size={12} />
              <span>
                {finalQty >= totalQty
                  ? 'Entire position will be closed at market price'
                  : formatNumber(finalQty, 2) + ' units will be closed at market price. ' + formatNumber(remainingQty, 2) + ' will remain open.'}
              </span>
            </div>
          )}

          {/* Validation error */}
          {error && (
            <div className="pm-warning" style={{ marginTop: '0.3rem', background: 'var(--pm-danger-light)', borderColor: 'rgba(239,68,68,0.15)', color: '#dc2626' }}>
              <AlertTriangle size={12} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn pm-btn--ghost" onClick={onClose} disabled={processing} style={{ fontSize: '0.78rem' }}>
            Cancel
          </button>
          <button
            className="pm-btn"
            onClick={handleConfirm}
            disabled={processing || finalQty <= 0}
            style={{ fontSize: '0.78rem', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff' }}
          >
            {processing ? (
              <><Loader2 size={12} className="pm-spin" /> Closing</>
            ) : (
              <><TrendingDown size={12} /> Close Position</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloseTradeModal;
